import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Snackbar,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  AccountBalance,
  TrendingUp,
  TrendingDown,
  Payments,
  Receipt,
  Download,
  AttachMoney,
  CreditCard,
  LocalAtm,
  Phone,
  RequestQuote,
  CheckCircle,
  Pending,
  Cancel,
  Timeline,
  DateRange,
  Analytics,
  Info,
  Add
} from '@mui/icons-material';


import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { brand, text, border } from '../theme/colors';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';

const PAYMENT_METHODS = {
  'ecocash': { name: 'EcoCash', icon: <Phone /> },
  'onemoney': { name: 'OneMoney', icon: <Phone /> },
  'bank_transfer': { name: 'Bank Transfer', icon: <AccountBalance /> },
  'mukuru': { name: 'Mukuru', icon: <AttachMoney /> },
  'worldremit': { name: 'WorldRemit', icon: <AttachMoney /> }
};

const COLORS = [brand.primary, '#43B581', '#FAA61A', '#F04747', brand.secondary];

const WITHDRAWAL_STEPS = ['Requested', 'Admin Review', 'Processing', 'Sent', 'Received'];

const getWithdrawalStepIndex = (status) => {
  switch (status) {
    case 'pending': return 0;
    case 'reviewing': case 'approved': return 1;
    case 'processing': return 2;
    case 'sent': return 3;
    case 'completed': return 4;
    case 'failed': case 'rejected': return -1;
    default: return 0;
  }
};

const ContractorFinancialHub = ({ role = 'contractor' }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Financial Data
  const [financialSummary, setFinancialSummary] = useState({
    total_earnings: 0,
    pending_payments: 0,
    available_balance: 0,
    withdrawn: 0,
    projects_completed: 0,
    average_project_value: 0
  });

  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);


  const [projectBreakdown, setProjectBreakdown] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  
  // Withdrawal Dialog
  const [withdrawalDialog, setWithdrawalDialog] = useState(false);
  const [withdrawalForm, setWithdrawalForm] = useState({
    amount: '',
    payment_method: 'ecocash',
    account_details: {
      phone: '',
      account_name: '',
      account_number: '',
      bank_name: ''
    },
    notes: ''
  });
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadFinancialData();
  }, [role]);

  const loadFinancialData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (role === 'verifier') {
        // Load verifier earnings data
        const response = await api.get('/reports/verifier/earnings');
        const data = response.data;

        if (!data.success) {
          throw new Error(data.message || 'Failed to load earnings data');
        }
        
        const report = data.data;
        
        const lifetimeCredited = parseFloat(report.summary.paidEarnings) || 0;
        const withdrawable = parseFloat(report.summary.withdrawableBalance) || 0;
        setFinancialSummary({
          total_earnings: report.summary.totalEarnings || 0,
          pending_payments: report.summary.pendingEarnings || 0,
          available_balance: withdrawable,
          withdrawn: Math.max(0, lifetimeCredited - withdrawable),
          projects_completed: report.summary.totalVerifications || 0,
          average_project_value: report.summary.averagePerVerification || 0
        });
        
        // Transform verifier payments to transactions format
        const payments = report.payments || [];
        const feeTierLabel = (type) => {
          const t = (type || '').toLowerCase();
          if (t === 'simple') return 'Simple ($75)';
          if (t === 'moderate') return 'Moderate ($100)';
          if (t === 'complex') return 'Complex ($150)';
          return type || 'Verification';
        };
        const paymentLabel = (payment) => {
          if (payment.request_type === 'contractor_verification') {
            return payment.contractor_name
              ? `Contractor verification — ${payment.contractor_name}`
              : 'Contractor verification';
          }
          return `Verification — ${feeTierLabel(payment.request_type)}`;
        };
        setTransactions(payments.map(payment => ({
          id: payment.id,
          date: payment.completion_date || payment.created_at,
          type: 'verification_payment',
          project_title: paymentLabel(payment),
          description: paymentLabel(payment),
          amount: parseFloat(payment.payment_amount) || 0,
          status: payment.payment_status || 'completed',
          milestone: payment.request_type
        })));
        
        // Generate monthly earnings from breakdown
        const monthlyArray = Object.entries(report.breakdown?.monthly || {})
          .map(([key, value]) => {
            try {
              const date = new Date(key + '-01');
              if (isNaN(date.getTime())) return null;
              return {
                month: date.toLocaleDateString('en-US', { month: 'short' }),
                earnings: parseFloat(value?.earnings || 0) || 0,
                projects: parseInt(value?.count || 0) || 0
              };
            } catch (e) {
              return null;
            }
          })
          .filter(item => item !== null)
          .sort((a, b) => {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return months.indexOf(a.month) - months.indexOf(b.month);
          });
        // Generate project breakdown from types
        const typesArray = Object.entries(report.breakdown?.byType || {})
          .map(([type, value]) => ({
            name: (type.charAt(0).toUpperCase() + type.slice(1)).replace(/_/g, ' '),
            value: parseFloat(value?.earnings || 0) || 0
          }));
        setProjectBreakdown(typesArray);
        
      } else {
        // Original contractor logic
        const [paymentsResponse, balanceResponse, withdrawalsResponse] = await Promise.all([
          api.get('/payments').catch(() => ({ data: { success: false, data: { payments: [] } } })),
          api.get('/payments/balance').catch(() => ({ data: { success: true, data: { available: 0 } } })),
          api.get('/payments/withdrawal-history').catch(() => ({ data: { success: true, data: [] } }))
        ]);

      // Process payments data
      const payments = paymentsResponse.data?.data?.payments || [];
      const completedPayments = payments.filter(p => p.status === 'completed' || p.status === 'released');
      const pendingPayments = payments.filter(p => p.status === 'pending' || p.status === 'approved');

      const totalEarnings = completedPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const pendingAmount = pendingPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const availableBalance = balanceResponse.data?.data?.available ?? null;

      setFinancialSummary({
        total_earnings: totalEarnings,
        pending_payments: pendingAmount,
        available_balance: availableBalance,
        withdrawn: totalEarnings - (availableBalance ?? 0) - pendingAmount,
        projects_completed: completedPayments.length,
        average_project_value: completedPayments.length > 0 ? totalEarnings / completedPayments.length : 0
      });

      // Transform payments to transactions
      setTransactions(payments.map(payment => ({
        id: payment.id,
        date: payment.created_at || payment.payment_date,
        type: payment.payment_type || 'milestone_payment',
        project_title: payment.project_title || payment.project_id,
        description: payment.description,
        amount: parseFloat(payment.amount) || 0,
        status: payment.status,
        milestone: payment.milestone_description
      })));

      // Generate earnings history from payments (last 7 months)
      const monthlyEarnings = {};
      const now = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
        monthlyEarnings[monthKey] = { month: monthKey, earnings: 0, projects: 0 };
      }

      completedPayments.forEach(payment => {
        const date = new Date(payment.created_at || payment.payment_date);
        const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
        if (monthlyEarnings[monthKey]) {
          monthlyEarnings[monthKey].earnings += parseFloat(payment.amount) || 0;
          monthlyEarnings[monthKey].projects += 1;
        }
      });

      setMonthlyData(Object.values(monthlyEarnings));

      // Group by project type for breakdown
      const breakdown = {};
      completedPayments.forEach(payment => {
        const type = payment.project_type || 'Other';
        if (!breakdown[type]) {
          breakdown[type] = { name: type, value: 0, count: 0 };
        }
        breakdown[type].value += parseFloat(payment.amount) || 0;
        breakdown[type].count += 1;
      });

      setProjectBreakdown(Object.values(breakdown));

      // Real withdrawal history
      const withdrawalHistory = withdrawalsResponse.data?.data || [];
      setWithdrawals(withdrawalHistory);
      }

    } catch (err) {
      console.error('Error loading financial data:', err);
      setError('Failed to load financial data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawalSubmit = async () => {
    if (!withdrawalForm.amount || parseFloat(withdrawalForm.amount) <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'warning' });
      return;
    }

    if (parseFloat(withdrawalForm.amount) < 10) {
      setSnackbar({ open: true, message: 'Minimum withdrawal amount is $10', severity: 'warning' });
      return;
    }

    if (parseFloat(withdrawalForm.amount) > financialSummary.available_balance) {
      setSnackbar({ open: true, message: 'Insufficient balance', severity: 'error' });
      return;
    }

    setSubmittingWithdrawal(true);

    try {
      const roleEndpoint = role === 'verifier' ? 'verifier' : 'contractor';
      await api.post(`/payments/${roleEndpoint}/${user?.id}/withdraw`, {
        amount: parseFloat(withdrawalForm.amount),
        paymentMethod: withdrawalForm.payment_method,
        paymentDetails: withdrawalForm.account_details,
        notes: withdrawalForm.notes
      });

      setSnackbar({ open: true, message: 'Withdrawal request submitted! Processing typically takes 1-3 business days.', severity: 'success' });
      setWithdrawalDialog(false);
      setWithdrawalForm({
        amount: '',
        payment_method: 'ecocash',
        account_details: {
          phone: '',
          account_name: '',
          account_number: '',
          bank_name: ''
        },
        notes: ''
      });
      loadFinancialData();
    } catch (err) {
      console.error('Error submitting withdrawal:', err);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to submit withdrawal request', severity: 'error' });
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'processing': return 'info';
      case 'failed': case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return `$${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderOverview = () => (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'white',
            color: text.primary,
            border: `2px solid ${border.light}`,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              borderColor: text.primary
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#43B581', fontWeight: 500, mb: 1 }}>
                    Available Balance
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: '#43B581' }}>
                    {financialSummary.available_balance !== null
                      ? formatCurrency(financialSummary.available_balance)
                      : 'Pending'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: text.secondary }}>
                    {financialSummary.available_balance !== null
                      ? 'Ready to withdraw'
                      : 'Processed on withdrawal request'}
                  </Typography>
                </Box>
                <AccountBalance sx={{ fontSize: 40, opacity: 0.3, color: '#43B581' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'white',
            color: text.primary,
            border: `2px solid ${border.light}`,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              borderColor: text.primary
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: brand.primary, fontWeight: 500, mb: 1 }}>
                    Total Earnings
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: brand.primary }}>
                    {formatCurrency(financialSummary.total_earnings)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingUp sx={{ fontSize: 16, color: text.secondary }} />
                    <Typography variant="caption" sx={{ color: text.secondary }}>
                      All time
                    </Typography>
                  </Box>
                </Box>
                <AttachMoney sx={{ fontSize: 40, opacity: 0.3, color: brand.primary }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'white',
            color: text.primary,
            border: `2px solid ${border.light}`,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              borderColor: text.primary
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#FAA61A', fontWeight: 500, mb: 1 }}>
                    Pending Payments
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: '#FAA61A' }}>
                    {formatCurrency(financialSummary.pending_payments)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: text.secondary }}>
                    Awaiting release
                  </Typography>
                </Box>
                <Pending sx={{ fontSize: 40, opacity: 0.3, color: '#FAA61A' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'white',
            color: text.primary,
            border: `2px solid ${border.light}`,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              borderColor: text.primary
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: brand.secondary, fontWeight: 500, mb: 1 }}>
                    Avg. Project Value
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5, color: brand.secondary }}>
                    {formatCurrency(financialSummary.average_project_value)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: text.secondary }}>
                    {financialSummary.projects_completed} projects
                  </Typography>
                </Box>
                <Analytics sx={{ fontSize: 40, opacity: 0.3, color: brand.secondary }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Earnings Chart */}
      {monthlyData.length > 0 && (
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${border.light}` }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: text.primary }}>
              Monthly Earnings (Last 7 Months)
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: text.secondary }} />
                <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 12, fill: text.secondary }} width={60} />
                <RechartsTooltip
                  formatter={(value) => [`$${value.toLocaleString()}`, 'Earnings']}
                  contentStyle={{ borderRadius: 8, border: `1px solid ${border.light}` }}
                />
                <Bar dataKey="earnings" fill={brand.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Earnings Breakdown by Project Type */}
      {projectBreakdown.length > 0 && (
        <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid ${border.light}` }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: text.primary }}>
              {role === 'verifier' ? 'Earnings by Verification Type' : 'Earnings Breakdown'}
            </Typography>
            <List disablePadding>
              {projectBreakdown.map((item, index) => (
                <ListItem key={index} sx={{ px: 0, borderBottom: index < projectBreakdown.length - 1 ? `1px solid ${border.light}` : 'none' }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: COLORS[index % COLORS.length] }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name || 'Other'}
                    secondary={`${item.count || 0} payment${(item.count || 0) !== 1 ? 's' : ''}`}
                    primaryTypographyProps={{ fontWeight: 600, color: text.primary }}
                    secondaryTypographyProps={{ color: text.secondary }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: text.primary }}>
                    {formatCurrency(item.value || 0)}
                  </Typography>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: text.secondary, fontWeight: 600 }}>
                Balance Summary
              </Typography>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="caption" sx={{ color: '#43B581', display: 'block' }}>
                  Available: {formatCurrency(financialSummary.available_balance)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#FAA61A', display: 'block' }}>
                  Pending: {formatCurrency(financialSummary.pending_payments)}
                </Typography>
                {financialSummary.withdrawn > 0 && (
                  <Typography variant="caption" sx={{ color: text.secondary, display: 'block' }}>
                    Withdrawn: {formatCurrency(financialSummary.withdrawn)}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Quick Action */}
      <Card sx={{
        backgroundColor: 'white',
        color: text.primary,
        mb: 4,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: `1px solid ${border.light}`
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: text.primary }}>
                Ready to withdraw your earnings?
              </Typography>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                You have {formatCurrency(financialSummary.available_balance)} available for withdrawal
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="large"
              startIcon={<RequestQuote />}
              onClick={() => setWithdrawalDialog(true)}
              sx={{
                borderColor: brand.primary,
                color: brand.primary,
                fontWeight: 700,
                borderWidth: 2,
                '&:hover': {
                  backgroundColor: brand.primary,
                  borderColor: brand.primary,
                  color: 'white'
                }
              }}
            >
              Request Withdrawal
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderTransactions = () => (
    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Transaction History
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box sx={{ py: 4 }}>
                      <Receipt sx={{ fontSize: 60, color: border.default, mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        No transactions yet
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>{formatDate(transaction.date)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {transaction.project_title}
                      </Typography>
                      {transaction.milestone && (
                        <Typography variant="caption" color="text.secondary">
                          {transaction.milestone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{transaction.description || transaction.type}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#43B581' }}>
                        {formatCurrency(transaction.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.status}
                        color={getStatusColor(transaction.status)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={50} sx={{ color: brand.primary }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary, mb: 1 }}>
          Financial Hub
        </Typography>
        <Typography variant="body1" sx={{ color: text.secondary }}>
          Track your earnings, manage withdrawals, and monitor your financial performance
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: border.light, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            '& .MuiTabs-indicator': { backgroundColor: brand.primary },
            '& .MuiTab-root.Mui-selected': { color: brand.primary, fontWeight: 600 },
            '& .MuiTab-root': { color: text.secondary }
          }}
        >
          <Tab label="Overview" />
          <Tab label="Transactions" />
          <Tab label="Withdrawals" />
        </Tabs>
      </Box>

      {activeTab === 0 && renderOverview()}
      {activeTab === 1 && renderTransactions()}
      {activeTab === 2 && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Withdrawal History
            </Typography>
            {withdrawals.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <LocalAtm sx={{ fontSize: 60, color: border.default, mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  No withdrawals yet
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {withdrawals.map((withdrawal) => {
                  const stepIndex = getWithdrawalStepIndex(withdrawal.status);
                  const isError = withdrawal.status === 'failed' || withdrawal.status === 'rejected';
                  const methodInfo = PAYMENT_METHODS[withdrawal.payment_method] || { name: withdrawal.payment_method, icon: <AttachMoney /> };

                  return (
                    <Paper key={withdrawal.id} sx={{ p: 3, borderRadius: 2, border: `1px solid ${border.light}` }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: isError ? '#F04747' : brand.primary, width: 36, height: 36 }}>
                            {methodInfo.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {formatCurrency(parseFloat(withdrawal.amount) || 0)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              via {methodInfo.name}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Chip
                            label={withdrawal.status}
                            color={isError ? 'error' : getStatusColor(withdrawal.status)}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {formatDate(withdrawal.created_at || withdrawal.requested_at || withdrawal.date)}
                          </Typography>
                        </Box>
                      </Box>

                      <Stepper
                        activeStep={isError ? WITHDRAWAL_STEPS.length : stepIndex}
                        alternativeLabel
                        sx={{
                          '& .MuiStepLabel-label': { fontSize: '0.75rem' },
                          '& .MuiStepIcon-root.Mui-active': { color: brand.primary },
                          '& .MuiStepIcon-root.Mui-completed': { color: isError ? '#F04747' : '#43B581' }
                        }}
                      >
                        {WITHDRAWAL_STEPS.map((label, index) => (
                          <Step key={label} completed={isError ? false : index < stepIndex}>
                            <StepLabel
                              error={isError}
                              StepIconProps={isError ? { error: true } : undefined}
                            >
                              {label}
                            </StepLabel>
                          </Step>
                        ))}
                      </Stepper>

                      {isError && withdrawal.failure_reason && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                          {withdrawal.failure_reason}
                        </Alert>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Withdrawal Dialog */}
      <Dialog open={withdrawalDialog} onClose={() => setWithdrawalDialog(false)} maxWidth="sm" fullWidth>
        <DialogCloseButton onClick={() => setWithdrawalDialog(false)} />
        <DialogTitle>Request Withdrawal</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Available balance: {formatCurrency(financialSummary.available_balance)}
            </Alert>

            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={withdrawalForm.amount}
              onChange={(e) => setWithdrawalForm(prev => ({ ...prev, amount: e.target.value }))}
              helperText="Minimum withdrawal: $10"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
              }}
            />

            <TextField
              fullWidth
              select
              label="Payment Method"
              value={withdrawalForm.payment_method}
              onChange={(e) => setWithdrawalForm(prev => ({ ...prev, payment_method: e.target.value }))}
              sx={{ mb: 2 }}
            >
              {Object.entries(PAYMENT_METHODS).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value.name}
                </MenuItem>
              ))}
            </TextField>

            {withdrawalForm.payment_method === 'ecocash' || withdrawalForm.payment_method === 'onemoney' ? (
              <TextField
                fullWidth
                label="Phone Number"
                value={withdrawalForm.account_details.phone}
                onChange={(e) => setWithdrawalForm(prev => ({
                  ...prev,
                  account_details: { ...prev.account_details, phone: e.target.value }
                }))}
                sx={{ mb: 2 }}
              />
            ) : withdrawalForm.payment_method === 'bank_transfer' ? (
              <>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={withdrawalForm.account_details.account_name}
                  onChange={(e) => setWithdrawalForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, account_name: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Account Number"
                  value={withdrawalForm.account_details.account_number}
                  onChange={(e) => setWithdrawalForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, account_number: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Bank Name"
                  value={withdrawalForm.account_details.bank_name}
                  onChange={(e) => setWithdrawalForm(prev => ({
                    ...prev,
                    account_details: { ...prev.account_details, bank_name: e.target.value }
                  }))}
                  sx={{ mb: 2 }}
                />
              </>
            ) : null}

            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={withdrawalForm.notes}
              onChange={(e) => setWithdrawalForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawalDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleWithdrawalSubmit}
            disabled={submittingWithdrawal}
            sx={{
              bgcolor: brand.primary,
              fontWeight: 700,
              '&:hover': { bgcolor: brand.primaryDark },
              '&:disabled': { bgcolor: border.light }
            }}
          >
            {submittingWithdrawal ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContractorFinancialHub;
